import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const EmailComposer = ({ open, onClose, recipients = [], userType = 'customer' }) => {
  const { t } = useTranslation();
  const [to, setTo] = useState(recipients.join(', '));
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [template, setTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const smtpdata = useSelector(state => state.smtpdata);

  useEffect(() => {
    if (recipients.length > 0) {
      setTo(recipients.join(', '));
    }
  }, [recipients]);

  const emailTemplates = {
    customer: [
      {
        id: 'booking_confirmation',
        name: t('booking_confirmation') || 'Booking Confirmation',
        subject: t('booking_confirmed') || 'Your booking has been confirmed',
        body: t('booking_confirmation_body') || 'Dear customer,\n\nYour booking has been confirmed. We look forward to serving you.\n\nBest regards,\nThe Team'
      },
      {
        id: 'welcome',
        name: t('welcome_email') || 'Welcome Email',
        subject: t('welcome_subject') || 'Welcome to our service!',
        body: t('welcome_body') || 'Dear customer,\n\nWelcome to our platform! We are excited to have you on board.\n\nBest regards,\nThe Team'
      },
      {
        id: 'custom',
        name: t('custom_email') || 'Custom Email',
        subject: '',
        body: ''
      }
    ],
    driver: [
      {
        id: 'new_booking',
        name: t('new_booking') || 'New Booking Alert',
        subject: t('new_booking_alert') || 'New booking available',
        body: t('new_booking_body') || 'Dear driver,\n\nA new booking is available. Please check your app for details.\n\nBest regards,\nThe Team'
      },
      {
        id: 'payment_received',
        name: t('payment_received') || 'Payment Received',
        subject: t('payment_received_subject') || 'Payment processed successfully',
        body: t('payment_received_body') || 'Dear driver,\n\nYour payment has been processed successfully.\n\nBest regards,\nThe Team'
      },
      {
        id: 'custom',
        name: t('custom_email') || 'Custom Email',
        subject: '',
        body: ''
      }
    ]
  };

  const handleTemplateChange = (event) => {
    const templateId = event.target.value;
    setTemplate(templateId);
    
    const templates = emailTemplates[userType] || emailTemplates.customer;
    const selectedTemplate = templates.find(t => t.id === templateId);
    
    if (selectedTemplate) {
      setSubject(selectedTemplate.subject);
      setMessage(selectedTemplate.body);
    }
  };

  const handleSend = async () => {
    setError('');
    setSuccess(false);

    if (!to || !subject || !message) {
      setError(t('fill_all_fields') || 'Please fill in all fields');
      return;
    }

    if (!smtpdata.smtpDetails || !smtpdata.smtpDetails.host) {
      setError(t('smtp_not_configured') || 'SMTP settings are not configured. Please configure SMTP in settings.');
      return;
    }

    setSending(true);

    try {
      // Call the backend API to send email
      const response = await fetch('/send_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: to.split(',').map(email => email.trim()),
          subject,
          message,
          smtp: smtpdata.smtpDetails
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          // Reset form
          setSubject('');
          setMessage('');
          setTemplate('');
          setSuccess(false);
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.error || t('failed_to_send') || 'Failed to send email');
      }
    } catch (err) {
      setError(t('error_sending_email') || 'Error sending email. Please try again.');
      console.error('Email send error:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="span">
          {t('compose_email') || 'Compose Email'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{t('email_sent_successfully') || 'Email sent successfully!'}</Alert>}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t('email_template') || 'Email Template'}</InputLabel>
            <Select
              value={template}
              onChange={handleTemplateChange}
              label={t('email_template') || 'Email Template'}
            >
              {(emailTemplates[userType] || emailTemplates.customer).map((tmpl) => (
                <MenuItem key={tmpl.id} value={tmpl.id}>
                  {tmpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('to') || 'To'}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            helperText={t('separate_emails_comma') || 'Separate multiple emails with commas'}
          />

          <TextField
            fullWidth
            label={t('subject') || 'Subject'}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <TextField
            fullWidth
            label={t('message') || 'Message'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={8}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button onClick={onClose} disabled={sending}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !to || !subject || !message}
          startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {sending ? (t('sending') || 'Sending...') : (t('send') || 'Send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailComposer;
